import { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import Drawer, { drawerClasses } from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import { ThemeProvider, useTheme } from "@mui/material/styles";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useDynamicCanvasTheme } from "@/forms-design-system/Tokens/dynamicCanvasTheme";
import { ChevronDown, ChevronUp, Cross } from "@/icons/outlined";

export type FormDrawerSize = "small" | "medium" | "large" | "full";

const DRAWER_SIZE_MAP: Record<FormDrawerSize, number | string> = {
  small: 450,
  medium: 650,
  large: 900,
  full: "100vw",
};

interface FormDrawerNavigationProps {
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export interface FormDrawerProps extends FormDrawerNavigationProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  anchor?: "left" | "right" | "top" | "bottom";
  size?: FormDrawerSize;
  width?: number | string;
  "aria-labelledby"?: string;
}

interface NavigationButtonsProps extends FormDrawerNavigationProps {
  navigationIconColor: string;
  navigationIconDisabledColor: string;
}

const NavigationButtons: FC<NavigationButtonsProps> = ({
  onPrevious,
  onNext,
  hasPrevious = true,
  hasNext = true,
  navigationIconColor,
  navigationIconDisabledColor,
}) => {
  const { t } = useTranslate();
  const hasNavigation = Boolean(onNext || onPrevious);
  if (!hasNavigation) {
    return null;
  }

  return (
    <Stack direction="row" gap={1}>
      {onNext && (
        <FormButton
          variant="ghost"
          iconButton
          startIcon={<ChevronDown />}
          disabled={!hasNext}
          onClick={onNext}
          aria-label={t("common", "a11y.nextItem")}
          sx={{ color: hasNext ? navigationIconColor : navigationIconDisabledColor }}
        />
      )}
      {onPrevious && (
        <FormButton
          variant="ghost"
          iconButton
          startIcon={<ChevronUp />}
          disabled={!hasPrevious}
          onClick={onPrevious}
          aria-label={t("common", "a11y.previousItem")}
          sx={{ color: hasPrevious ? navigationIconColor : navigationIconDisabledColor }}
        />
      )}
    </Stack>
  );
};

interface CloseButtonProps {
  onClose: () => void;
  closeIconColor: string;
}

const CloseButton: FC<CloseButtonProps> = ({ onClose, closeIconColor }) => {
  const { t } = useTranslate();

  return (
    <FormButton
      variant="ghost"
      iconButton
      startIcon={<Cross />}
      onClick={onClose}
      aria-label={t("common", "close")}
      sx={{ color: closeIconColor }}
    />
  );
};

interface FormDrawerHeaderProps extends FormDrawerNavigationProps {
  onClose: () => void;
}

const FormDrawerHeader: FC<FormDrawerHeaderProps> = ({ onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
  const theme = useTheme();
  const drawerTokens = theme.palette.formsComponents.drawer;

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
      <CloseButton onClose={onClose} closeIconColor={drawerTokens.closeIconColor} />
      <NavigationButtons
        onNext={onNext}
        onPrevious={onPrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        navigationIconColor={drawerTokens.navigationIconColor}
        navigationIconDisabledColor={drawerTokens.navigationIconDisabledColor}
      />
    </Stack>
  );
};

interface FormDrawerFooterProps extends FormDrawerNavigationProps {
  onClose: () => void;
}

const FormDrawerFooter: FC<FormDrawerFooterProps> = ({ onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
  const theme = useTheme();
  const drawerTokens = theme.palette.formsComponents.drawer;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 1,
        backgroundColor: drawerTokens.backgroundColor,
        paddingTop: theme.spacing(2),
        paddingBottom: "max(12px, env(safe-area-inset-bottom))",
        flexShrink: 0,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: theme.spacing(-3),
          right: theme.spacing(-3),
          borderTop: `1px solid ${drawerTokens.borderColor}`,
        },
      }}
    >
      <CloseButton onClose={onClose} closeIconColor={drawerTokens.closeIconColor} />
      <NavigationButtons
        onNext={onNext}
        onPrevious={onPrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        navigationIconColor={drawerTokens.navigationIconColor}
        navigationIconDisabledColor={drawerTokens.navigationIconDisabledColor}
      />
    </Stack>
  );
};

const InnerFormDrawer: FC<FormDrawerProps> = ({
  open,
  onClose,
  children,
  anchor = "right",
  size = "medium",
  width,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  "aria-labelledby": ariaLabelledBy,
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();

  if (!theme.palette.forms || !theme.palette.formsComponents?.drawer) {
    return <InvalidThemeErrorMessage />;
  }

  const drawerTokens = theme.palette.formsComponents.drawer;
  const paperWidth = isSmallScreen ? "100vw" : (width ?? DRAWER_SIZE_MAP[size]);

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={{ role: "dialog", "aria-modal": true, "aria-labelledby": ariaLabelledBy }}
      sx={{
        [`& .MuiBackdrop-root`]: {
          backgroundColor: drawerTokens.backdropColor,
        },
        [`& .${drawerClasses.paper}`]: {
          width: paperWidth,
          maxWidth: "100%",
          backgroundColor: drawerTokens.backgroundColor,
          ...(isSmallScreen && {
            height: "100vh",
            ["@supports (height: 100dvh)"]: {
              height: "100dvh",
            },
          }),
        },
      }}
    >
      <Stack
        sx={{
          height: "100%",
          flexWrap: "nowrap",
          overflow: "hidden",
          padding: theme.spacing(3, 3, isSmallScreen ? 0 : 2, 3),
          gap: isSmallScreen ? 0 : "20px",
        }}
      >
        {!isSmallScreen && (
          <FormDrawerHeader
            onClose={onClose}
            onNext={onNext}
            onPrevious={onPrevious}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
          />
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            ...(isSmallScreen
              ? {
                  padding: theme.spacing(0, 0, 0, 0),
                  marginRight: theme.spacing(-3),
                  paddingRight: theme.spacing(3),
                }
              : { padding: theme.spacing(0, 3) }),
            color: theme.palette.forms.text.moderate,
            scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`,
            ["&::-webkit-scrollbar-track"]: {
              background: "transparent",
            },
            ["&::-webkit-scrollbar-thumb"]: {
              background: theme.palette.formsComponents.global.scrollbarThumbColor,
            },
          }}
        >
          {children}
        </Box>

        {isSmallScreen && (
          <FormDrawerFooter
            onClose={onClose}
            onNext={onNext}
            onPrevious={onPrevious}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
          />
        )}
      </Stack>
    </Drawer>
  );
};

export const FormDrawer: FC<FormDrawerProps> = (props) => {
  const theme = useTheme();

  if (!theme.palette.forms || !theme.palette.metadata) {
    return <InvalidThemeErrorMessage />;
  }

  const mode = theme.palette.metadata.mode ?? "light";
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const drawerTheme = useDynamicCanvasTheme(theme.palette.metadata.baseColor, mode);

  return (
    <ThemeProvider theme={drawerTheme}>
      <InnerFormDrawer {...props} />
    </ThemeProvider>
  );
};
